import { useEffect } from 'react';
// types
import {
  IAptrinsic,
  IGainsightAccount,
  IGainsightProps,
  IGainsightUser,
} from './interfaces';
// toggles
import { ANALYTICS_TRACKING } from 'src/app/featureToggles';
import { useConfigSelectors } from 'src/app/reducers';
// functions
import { getUrlDetails } from 'src/app/functions/core';
import { parseJwt } from 'src/utils/jwt';
import { sha256Hex } from 'src/utils/hash';
import { appVersion, gainsightDryRun, gainsightEnabled } from 'src/config';

// We need a handle to the Gainsight PX global here, so let's declare it and
// make TS happy.
declare global {
  // tslint:disable-next-line: interface-name
  interface Window {
    aptrinsic?: IAptrinsic;
  }
}

// Methods exposed by the Gainsight PX web SDK
type IGainsightAPIMethods = 'identify' | 'track' | 'set' | 'reset';

// Ideagen uses the EU Gainsight PX instance
const GAINSIGHT_SDK_URL = 'https://web-sdk-eu.aptrinsic.com/api/aptrinsic.js';

// NoiseDesk accounts are not yet in Ideagen's Salesforce instance, so ids are
// product scoped. Prefix per the Gainsight PX specification.
const PRODUCT_PREFIX = 'NoiseDesk';

// All NoiseDesk users authenticate through Azure AD B2C
const AUTH_METHOD = 'SSO';

const LOG_PREFIX = '[Gainsight PX dry run]';

/**
 * Reports what would have been sent to Gainsight PX while the dry run flag is
 * set. No-ops when the flag is off, so call sites need no further guarding.
 */
const logDryRun = (message: string, detail?: unknown): void => {
  if (!gainsightDryRun) {
    return;
  }
  if (detail === undefined) {
    console.log(`${LOG_PREFIX} ${message}`);
  } else {
    console.log(`${LOG_PREFIX} ${message}`, detail);
  }
};

const DOMReady = !!(
  typeof window !== 'undefined' &&
  window.document &&
  window.document.createElement
);

/**
 * Stub that queues calls made before the SDK script has finished loading.
 * The real SDK drains the queue once it loads.
 */
const createQueueStub = (): IAptrinsic => {
  const stub: IAptrinsic = (...args: unknown[]): void => {
    stub.q = stub.q ?? [];
    stub.q.push(args);
  };
  return stub;
};

/**
 * Injects the Gainsight PX tag. Must only run once per page load; calling it
 * repeatedly in a single page app causes events to be logged more than once.
 */
const GainsightCodeInjection = (productTag: string): void => {
  if (!DOMReady) {
    return;
  }
  if (gainsightDryRun) {
    logDryRun('tag not injected, no SDK loaded', {
      productTag,
      wouldRequest: `${GAINSIGHT_SDK_URL}?a=${productTag}`,
    });
    return;
  }

  const aptrinsic = window.aptrinsic ?? createQueueStub();
  aptrinsic.p = productTag;
  window.aptrinsic = aptrinsic;

  const script = document.createElement('script');
  script.async = true;
  script.src = `${GAINSIGHT_SDK_URL}?a=${productTag}`;

  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript?.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
};

/**
 * Provides access to the Gainsight PX API for use in the app.
 * Note: custom events and global context must be approved by the application
 * security team before use, as they may leak data.
 */
export const GainsightAPI = (method: IGainsightAPIMethods, ...args: unknown[]): void => {
  if (!gainsightEnabled) {
    return;
  }
  if (gainsightDryRun) {
    logDryRun(`"${method}" not sent, payload was`, args);
    return;
  }
  if (DOMReady && window.aptrinsic) {
    window.aptrinsic(method, ...args);
  }
};

export const Gainsight = ({ productTag, authData }: IGainsightProps): null => {
  const configSelectors = useConfigSelectors();
  const hasAnalyticsTrackingFeature = configSelectors.isFeatureAvailable(ANALYTICS_TRACKING);
  const doNotTrack = DOMReady && !!window.navigator.doNotTrack;

  const { siteName } = getUrlDetails();
  const authorization = authData?.authorization;
  const isAnonymous = authData?.isAnonymous;

  // No product tag means the integration is fully off, so nothing is loaded,
  // nothing is sent and nothing is logged.
  const enabled = !!productTag && productTag.trim() !== '';

  useEffect(() => {
    if (!enabled || doNotTrack || (DOMReady && window.aptrinsic)) {
      return;
    }
    GainsightCodeInjection(productTag);
  }, [enabled, productTag, doNotTrack]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    // Each remaining gate reports itself so a dry run explains its own silence
    // rather than looking broken. logDryRun no-ops when the flag is off.
    if (doNotTrack) {
      logDryRun('identify skipped, browser sent Do Not Track');
      return;
    }
    if (!hasAnalyticsTrackingFeature) {
      logDryRun(`identify skipped, "${ANALYTICS_TRACKING}" feature toggle is off`);
      return;
    }
    if (isAnonymous) {
      logDryRun('identify skipped, user is anonymous');
      return;
    }
    if (!authorization) {
      logDryRun('identify skipped, no authorization token');
      return;
    }
    if (!siteName) {
      logDryRun('identify skipped, no site name in the URL');
      return;
    }

    let cancelled = false;
    void getIdentity(authorization, siteName).then(identity => {
      if (cancelled) {
        return;
      }
      if (!identity) {
        logDryRun('identify skipped, could not derive a user id from the token');
        return;
      }
      GainsightAPI('identify', identity.user, identity.account);
    });

    return () => {
      cancelled = true;
    };
  }, [authorization, isAnonymous, siteName, hasAnalyticsTrackingFeature, enabled, doNotTrack]);

  return null;
};

/**
 * Builds the user and account identification payloads from the access token.
 * Returns null when no stable user id can be derived.
 */
export const getIdentity = async (
  authorization: string,
  siteName: string
): Promise<{ user: IGainsightUser; account: IGainsightAccount } | null> => {
  const authString = authorization.split(' ')[1];
  if (!authString) {
    return null;
  }

  const authObject = parseJwt(authString);
  const email = getEmail(authObject);
  const id = await getUserId(authObject, email);
  if (!id) {
    return null;
  }

  const user: IGainsightUser = {
    id,
    appversion: String(appVersion ?? ''),
    userauthmethod: AUTH_METHOD,
  };
  if (email) {
    user.email = email;
  }

  return { user, account: getAccount(siteName) };
};

/**
 * The lowercased email address from the token, or null when the user has none.
 */
const getEmail = (authObject: Record<string, unknown>): string | null => {
  const { email, otherMails } = authObject as { email?: string; otherMails?: string[] };
  if (email) {
    return email.toLowerCase();
  }
  console.warn('user does not have defined email');
  if (otherMails?.length) {
    return otherMails[0].toLowerCase();
  }
  return null;
};

/**
 * The Gainsight PX user id. Where an email address is known this must be a
 * lowercase, non-delimited SHA-256 hash of the lowercased address. Otherwise it
 * must be an id unique to this application and this user.
 */
const getUserId = async (
  authObject: Record<string, unknown>,
  email: string | null
): Promise<string | null> => {
  if (email) {
    return sha256Hex(email);
  }
  const { sub } = authObject as { sub?: string };
  return sub ? `${PRODUCT_PREFIX}-${sub}` : null;
};

/**
 * The Gainsight PX account (the customer). NoiseDesk site names are of the form
 * `<customer>.<location>` (e.g. `asa.bne`), and one customer may have several
 * locations, so the account is keyed on the customer portion alone.
 *
 * Replace this with the Salesforce account id and name, and add the
 * parentGroupId and sfdcId fields, once NoiseDesk customers are present in
 * Ideagen's Salesforce instance.
 */
export const getAccount = (siteName: string): IGainsightAccount => {
  const customer = siteName.includes('.') ? siteName.split('.')[0] : siteName;
  return {
    id: `${PRODUCT_PREFIX}-${customer}`,
    name: customer,
  };
};
